import { Button, Card, Flex, Form, Input, message } from "antd";
import { useForm } from "antd/es/form/Form";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import WarehouseService from "../../services/Warehouses/WarehouseService";
import WarehouseDockDelete from "./WarehouseDockDelete";
import AppButton from "../../common/AppButton";

const WarehouseDockForm = ({ warehouseId, dockId, handleIsModifying, handleDockIdChange }) => {
  const [form] = useForm();
  const queryClient = useQueryClient();

  const handleCancel = () => {
    form.resetFields();
    handleIsModifying();
    handleDockIdChange(null);
  };

  const reset = async () => {
    await queryClient.invalidateQueries({ queryKey: [warehouseId, "docks"] });
    await queryClient.invalidateQueries({ queryKey: [warehouseId, "docks", dockId] });
    await queryClient.invalidateQueries({ queryKey: [warehouseId, "receivingZones"] });
    handleCancel();
  };

  const { mutate: addNewDocks } = useMutation({
    mutationFn: async (request) =>
      await WarehouseService.addNewDocks(warehouseId, request),
    onSuccess: async () => {
      await reset();
      message.success("Dock added successfully!");
    },
    onError: async (error) => {
      message.error(`Failed to add dock. ${error.response?.data?.message}, Please try again.`);
    },
  });

  const { mutate: updateDock } = useMutation({
    mutationFn: async (request) =>
      await WarehouseService.updateDock(warehouseId, dockId, request),
    onSuccess: async () => {
      await reset();
      message.success("Dock updated successfully!");
    },
    onError: async (error) => {
      message.error(`Failed to update dock. ${error.response?.data?.message}, Please try again.`);
    },
  });

  const cachedDock = queryClient.getQueryData([warehouseId, "docks"])
    ?.find(dock => dock.id === +dockId);
  if (cachedDock) {
    queryClient.setQueryData([warehouseId, "docks", dockId], cachedDock);
  }
  const { data: dock } = useQuery({
    queryKey: [warehouseId, "docks", dockId],
    queryFn: async () => await WarehouseService.getDockById(warehouseId, dockId),
    enabled: !!dockId && !cachedDock,
  });

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      if (dockId) {
        updateDock(values);
      } else {
        addNewDocks([values]);
      }
    } catch {
      // Do nothing, let the form handle the error messages
    }
  };

  const title = !dockId ? "Add Dock" : "Edit Dock Details";

  return (
    <Card title={title} className="card-form">
      <Form form={form}
            initialValues={dock}
            layout="vertical">
        <Flex justify="space-between" align="start" gap={8}>
          <Form.Item label="Dock Name"
                     rules={[{ required: true, message: "Please enter dock name" }]}
                     name="name">
            <Input size="large" />
          </Form.Item>
          <Form.Item label="Short Code"
                     normalize={(value) => value?.trim()}
                     rules={[
                       { required: true, message: "Please enter dock code" },
                       { max: 10, message: "Code must be 10 characters or less" },
                     ]}
                     name="code">
            <Input size="large" />
          </Form.Item>
        </Flex>
        <Flex justify="end" align="center" gap={8} className="mt-4">
          <AppButton variant="save" onClick={handleSubmit}>
                Save
        </AppButton>
          {dockId &&
            <WarehouseDockDelete id={dockId}
                                 handleCancel={handleCancel}
                                 warehouseId={warehouseId} />
          }
        <AppButton variant="close" onClick={handleCancel}>
                Close
        </AppButton>
        </Flex>
      </Form>
    </Card>
  );
};

export default WarehouseDockForm;